// Shared tooltip + escape helpers for every allocation chart type
// (donut, bar, treemap). Lifting these out keeps tooltip wording and
// HTML markup identical across the three charts so toggling between
// them is purely a visual swap, not a wording drift surface.
//
// `buildSliceTooltipFormatter` returns an echarts-compatible formatter
// callback. The shape of `params` differs per series type:
//   - pie:      { data: { name, raw, percent }, color }
//   - bar:      { data: { name, raw, percent }, color, name, value }
//   - treemap:  { data: { name, raw, percent }, color, value, name }
// All of them expose `params.data` (our augmented slice object) and
// `params.color`, so we can read from the same fields uniformly.

export function escapeHtml (s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * @param {(value: number, format?: string) => string} fmt -- the
 *   formatting-context `formatter` so signed values render the same
 *   way grid cells do.
 * @param {string} valueFormat -- the numeral-style format string for
 *   the active measure (e.g. effectiveFormat output).
 * @returns echarts tooltip `formatter` callback.
 */
export function buildSliceTooltipFormatter (fmt, valueFormat) {
  return function tooltipFormatter (params) {
    const slice = params?.data || {}
    const rawValue = typeof slice.raw === 'number' ? slice.raw : (typeof params?.value === 'number' ? params.value : 0)
    const percent = typeof slice.percent === 'number' ? slice.percent : 0
    const valueText = fmt(rawValue, valueFormat)
    const pctText = `${(percent * 100).toFixed(2)}%`
    return `
      <div style="display:flex;flex-direction:column;gap:4px;font-size:12px;">
        <div style="display:flex;align-items:center;gap:6px;font-weight:600;">
          <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${params.color};"></span>
          <span>${escapeHtml(slice.name)}</span>
        </div>
        <div>${valueText}</div>
        <div style="color:var(--color-neutral-500,#888);">${pctText} of total</div>
      </div>
    `
  }
}
